import React, { useState, useEffect } from "react";
import moment from "moment";
import { DatePicker } from "@mantine/dates";
import {
	IconTrash,
	IconCheck,
	IconCircleCheck,
	IconAlertOctagon,
	IconPencil,
	IconCross,
} from "@tabler/icons";
import _ from "lodash";

import {
	Switch,
	TextInput,
	Select,
	Textarea,
	NumberInput,
	Radio,
	Group,
	Text,
} from "@mantine/core";
import {
	DATA_GENDERS,
	DATA_ID_TYPE,
	DATA_MARKETING_PREFERENCE,
	DATA_RELATIONSHIP,
} from "../data/options";

import { ENUM_FORM_DISPLAY, ENUM_FORM_LAYOUT_CONTAINER } from "./pageList";
import LocalPageList from "./localPageList";
import FileList from "./fileList";
import ActionCellRender from "./actionCellRender";
import RelatedPersonClientLookup from "./relatedPersonClientLookup";
import { stripLinkedPersonIdentity } from "../helpers/clientToRelatedPerson";
import { linkedClientRecord, useLinkedClients } from "../hooks/useLinkedClients";
import { formatPhoneDisplay } from "../helpers/phoneUtils";
import { useFormatter } from "../hooks/useFomatter";

import { useServerApi } from "../hooks/userServerApi";
import ReactJson from "react-json-view";

const BeneficiaryTypeRadio = ({ form, value, onChange, label, ...props }) => {
	const selected = value ? "contingent" : "primary";

	return (
		<Radio.Group
			{...props}
			label={label}
			value={selected}
			onChange={(next) => {
				const isContingent = next === "contingent";
				onChange(isContingent);
				form?.setFieldValue("isPrimary", !isContingent);
			}}
		>
			<Group mt="xs">
				<Radio value="primary" label="Primary" />
				<Radio value="contingent" label="Contingent" />
			</Group>
		</Radio.Group>
	);
};

const OrderBeneficiaryList = ({ form: mainForm, ...props }) => {
	const orderId = _.get(mainForm.values, "_id");
	const [formatter] = useFormatter();

	const [rows, setRows] = useState([]);
	const [api] = useServerApi();
	const linkedClients = useLinkedClients(rows);
	const displayPerson = (person) =>
		linkedClientRecord(person?.sourceClient, linkedClients) || person || {};

	const tableSchema = {
		hidePagination: true,
		columns: [
			// {   field: '_id',       headerName:"ID"            },
			{
				field: "name",
				cellRender: (col, data) => displayPerson(data).name || "",
			},
			{
				field: "cname",
				headerName: "Chinese Name",
				cellRender: (col, data) => displayPerson(data).cname || "",
			},

			{
				field: "phone",
				cellRender: (col, data) => {
					const person = displayPerson(data);
					return formatPhoneDisplay(person.phone) || person.phone || "";
				},
			},

			{
				field: "percentage",
				cellRender: (col, data) => formatter.percentage(data.percentage),
			},

			{
				field: "isPrimary",
				headerName: "Type",
				cellRender: (col, data) =>
					data.isBackup ? "Contingent" : data.isPrimary === false ? "—" : "Primary",
			},

			{
				field: "trustee.name",
				headerName: "Trustee",
				cellRender: (col, data) => displayPerson(data?.trustee).name || "--",
			},

			{
				cellRender: ActionCellRender,
				isAction: true,
				actions: [
					{
						name: "edit",
						label: "Edit",
						icon: IconPencil,
						props: {},
					},
					{
						name: "delete",
						label: "Delete data",
						icon: IconTrash,
						props: {},
					},
				],
				actionMenu: [],
			},
		],
		searchableFields: ["code", "name", "cname", "email"],
	};

	const formSchema = {
		title: "Beneficiary Detail",
		description: "Please fill in the form",

		display: {
			mode: ENUM_FORM_DISPLAY.MODAL,
			size: "xl",
		},
		initialValues: {
			trustee: {},
		},

		layout: {
			containers: [
				{
					key: "main-tab",
					parent: null,
					type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
					props: {
						variant: "default", //default | pills | outline
						defaultValue: "Beneficiary",
					},
					tab: [
						{
							key: "tab-beneficiary",
							label: "Beneficiary",
						},
						{
							key: "tab-trustee",
							label: "Trustee",
							showTab: (data) => !data.sameAsClient,
						},
						{
							key: "tab-files",
							label: "Files",
						},
					],
				},
				{
					key: "pre",
					parent: "tab-beneficiary",
					type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
				},

				{
					key: "main",
					parent: "tab-beneficiary",
					visibleOnly: (data) => !data.sameAsClient,

					type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
					cols: [
						{
							key: "g1",
							props: {
								span: 6,
							},
						},
						{
							key: "g2",
							props: {
								span: 6,
							},
						},
					],
				},
				{
					key: "trustee-section",
					parent: "tab-trustee",
					visibleOnly: (data) => !data.sameAsClient,
					type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
					props: {
						title: "Trustee (only applicable to beneficiary under 18)",
					},
				},
				{
					key: "trustee-lookup",
					parent: "trustee-section",
					type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
				},
				{
					key: "trustee-grid",
					parent: "trustee-section",
					type: ENUM_FORM_LAYOUT_CONTAINER.BOX,
				},
				{
					key: "file-section",
					parent: "tab-files",
					type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
					props: {
						title: "Files",
					},
				},
			],

			fields: [
				{
					name: "sameAsClient",
					parent: "pre",

					component: Switch,
					props: {
						label: "Same as applicant",
						type: "checkbox",

						mb: 30,
					},
				},
				{
					name: "sourceClient",
					component: RelatedPersonClientLookup,
					parent: "pre",
					visibleOnly: (data) => !data.sameAsClient,
					props: {
						subject: "person",
					},
				},
				{
					name: "name",
					component: TextInput,
					parent: "g1",
					visibleOnly: (data) => !data.sourceClient && !data.identityFromClient,
					props: {
						required: (data) => !data.sourceClient && !data.identityFromClient,
						label: "English Name",
					},
				},

				{
					name: "cname",
					component: TextInput,
					parent: "g1",
					visibleOnly: (data) => !data.sourceClient && !data.identityFromClient,
					props: {
						required: (data) => !data.sourceClient && !data.identityFromClient,
						label: "Chinese Name",
					},
				},

				{
					name: "percentage",
					component: NumberInput,
					parent: "g2",
					props: {
						required: true,
						label: "Percentage",
						placeholder: "",
					},
				},
				{
					name: "idType",
					component: Select,
					parent: "g1",
					visibleOnly: (data) => !data.sourceClient && !data.identityFromClient,
					props: {
						label: "ID Type",
						placeholder: "",
						data: DATA_ID_TYPE,
						searchable: true,
						clearable: true,
					},
				},
				{
					name: "id",
					component: TextInput,
					parent: "g1",
					visibleOnly: (data) => !data.sourceClient && !data.identityFromClient,
					props: {
						required: (data) => !data.sourceClient && !data.identityFromClient,
						label: "ID No.",
					},
				},
				{
					name: "email",
					component: TextInput,
					parent: "g1",
					visibleOnly: (data) => !data.sourceClient && !data.identityFromClient,
					props: {
						label: "Email",
						placeholder: "",
					},
				},

				{
					name: "relationship",
					component: Select,
					parent: "g1",
					props: {
						required: true,
						label: "Relationship with insured",
						placeholder: "",
						data: DATA_RELATIONSHIP,
						multiple: false,
						searchable: true,
						clearable: true,
					},
				},

				{
					name: "remark",
					component: Textarea,
					parent: "g1",
					props: {
						required: true,
						label: "Remark",
					},
				},
				{
					name: "gender",
					component: Select,
					parent: "g2",
					visibleOnly: (data) => !data.sourceClient && !data.identityFromClient,
					props: {
						required: (data) => !data.sourceClient && !data.identityFromClient,
						label: "Gender",
						placeholder: "",
						data: DATA_GENDERS,
						multiple: true,
						searchable: true,
						clearable: true,
					},
				},
				{
					name: "phone",
					component: TextInput,
					parent: "g2",
					visibleOnly: (data) => !data.sourceClient && !data.identityFromClient,
					props: {
						required: (data) => !data.sourceClient && !data.identityFromClient,
						label: "Contact Tel.",
						placeholder: "",
					},
				},
				{
					name: "birthday",
					component: DatePicker,
					parent: "g2",
					type: "datePicker",
					visibleOnly: (data) => !data.sourceClient && !data.identityFromClient,
					props: {
						required: (data) => !data.sourceClient && !data.identityFromClient,
						label: "Date of Birth",
						zIndex: 1000,
						placeholder: "Pick Date",
						allowFreeInput: true,
					},
				},
				{
					name: "address",
					component: Textarea,
					parent: "g2",
					visibleOnly: (data) => !data.sourceClient && !data.identityFromClient,
					props: {
						label: "Address",
						minRows: 2,
					},
				},
				{
					name: "marketingPreference",
					component: Select,
					parent: "g2",
					visibleOnly: (data) => !data.sourceClient && !data.identityFromClient,
					props: {
						label: "Marketing Preference",
						placeholder: "",
						data: DATA_MARKETING_PREFERENCE,
						searchable: true,
						clearable: true,
					},
				},
				{
					name: "isBackup",
					component: BeneficiaryTypeRadio,
					parent: "g2",
					props: {
						required: true,
						label: "Beneficiary type",
					},
				},
				{
					name: "trustee.sourceClient",
					component: RelatedPersonClientLookup,
					parent: "trustee-lookup",
					props: {
						prefix: "trustee",
						subject: "trustee",
					},
				},
				{
					name: "trustee.name",
					component: TextInput,
					parent: "trustee-grid",
					visibleOnly: (data) => !data.trustee?.sourceClient && !data.trustee?.identityFromClient,
					props: {
						label: "Trustee name",
					},
				},
				{
					name: "trustee.idType",
					component: Select,
					parent: "trustee-grid",
					visibleOnly: (data) => !data.trustee?.sourceClient && !data.trustee?.identityFromClient,
					props: {
						label: "Trustee ID Type",
						placeholder: "",
						data: DATA_ID_TYPE,
						searchable: true,
						clearable: true,
					},
				},
				{
					name: "trustee.id",
					component: TextInput,
					parent: "trustee-grid",
					visibleOnly: (data) => !data.trustee?.sourceClient && !data.trustee?.identityFromClient,
					props: {
						label: "Trustee ID No.",
					},
				},
				{
					name: "trustee.email",
					component: TextInput,
					parent: "trustee-grid",
					visibleOnly: (data) => !data.trustee?.sourceClient && !data.trustee?.identityFromClient,
					props: {
						label: "Trustee Email",
					},
				},
				{
					name: "trustee.relationship",
					component: Select,
					parent: "trustee-grid",
					props: {
						label: "Trustee relationship with insured",
						placeholder: "",
						data: DATA_RELATIONSHIP,
						multiple: false,
						searchable: true,
						clearable: true,
					},
				},
				{
					name: "trustee.address",
					component: Textarea,
					parent: "trustee-grid",
					visibleOnly: (data) => !data.trustee?.sourceClient && !data.trustee?.identityFromClient,
					props: {
						label: "Trustee Address",
						minRows: 2,
					},
				},
				{
					name: "trustee.marketingPreference",
					component: Select,
					parent: "trustee-grid",
					visibleOnly: (data) => !data.trustee?.sourceClient && !data.trustee?.identityFromClient,
					props: {
						label: "Trustee Marketing Preference",
						placeholder: "",
						data: DATA_MARKETING_PREFERENCE,
						searchable: true,
						clearable: true,
					},
				},
				{
					name: "files",
					component: FileList,
					parent: "file-section",
					props: {},
				},
			],
		},
	};

	const onFormSubmit = (values) => {
		const next = (values || []).map(stripLinkedPersonIdentity);
		setRows(next);
		mainForm.setFieldValue("beneficiary", next);

		// _.set(mainForm.values, "beneficiary", values); //Update MainForm to prevent further override

		// api.Client.updateRiskAML(clientID, values); //update server
	};

	const initRowData = async () => {
		if (!orderId) return;
		setRows(mainForm.values?.beneficiary);
	};

	useEffect(() => {
		initRowData();
	}, [orderId]);

	return (
		<>
			{/* <ReactJson
				src={mainForm.values}
				collapsed
				style={{ background: "white" }}
			/> */}
			<LocalPageList
				rows={rows}
				onFormSubmit={onFormSubmit}
				tableSchema={tableSchema}
				formSchema={formSchema}
				{...props}
				btnLabel="Add Beneficiary"
			/>
		</>
	);
};

export default OrderBeneficiaryList;
